/**
 * Seletores derivados do estado.
 *
 * Zustand v5 usa `useSyncExternalStore`, que compara o retorno do selector por
 * identidade. Um selector que monta um array novo a cada chamada (com `?? []`,
 * `.filter()` ou `Object.values()`) faz o React ver mudança em toda checagem e
 * entrar em laço infinito, derrubando a árvore.
 *
 * O padrão correto, usado em todos os hooks aqui: o selector devolve apenas
 * referências que já existem no estado (um dicionário, uma entrada dele), e a
 * derivação acontece num `useMemo` dependente dessas referências. Assim a
 * identidade só muda quando o dado realmente muda.
 *
 * A alternativa de chamar uma função derivada fora do selector tem o defeito
 * oposto: não re-renderiza quando o dado muda, porque nada foi assinado.
 */

import { useMemo } from 'react'
import type { Category, Channel, GuildMember, Message, VoiceState } from '@nexus/shared'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'

/** Referências estáveis para os casos vazios, para não recriar a cada render. */
const EMPTY_MESSAGES: Message[] = []
const EMPTY_VOICE: VoiceState[] = []
const EMPTY_MEMBERS: GuildMember[] = []
const EMPTY_IDS: string[] = []

export function useChannelMessages(channelId: string): Message[] {
  // O `??` fica fora do selector de propósito: dentro dele, criaria um array
  // novo sempre que o canal ainda não tivesse mensagens carregadas.
  const messages = useAppStore((s) => s.messages[channelId])
  return messages ?? EMPTY_MESSAGES
}

export function useChannelVoiceStates(channelId: string | null): VoiceState[] {
  const voiceStates = useAppStore((s) => s.voiceStates)

  return useMemo(() => {
    if (!channelId) return EMPTY_VOICE
    const list = Object.values(voiceStates).filter((v) => v.channelId === channelId)
    return list.length > 0 ? list : EMPTY_VOICE
  }, [voiceStates, channelId])
}

export interface GuildChannelTree {
  categories: Category[]
  byCategory: Record<string, Channel[]>
  uncategorized: Channel[]
}

export function useGuildChannelTree(guildId: string | null): GuildChannelTree {
  const channels = useAppStore((s) => s.channels)
  const categories = useAppStore((s) => s.categories)

  return useMemo(() => {
    if (!guildId) return { categories: [], byCategory: {}, uncategorized: [] }

    const guildCategories = Object.values(categories)
      .filter((c) => c.guildId === guildId)
      .sort((a, b) => a.position - b.position)

    const guildChannels = Object.values(channels)
      .filter((c) => c.guildId === guildId)
      .sort((a, b) => a.position - b.position)

    const byCategory: Record<string, Channel[]> = {}
    const uncategorized: Channel[] = []

    for (const channel of guildChannels) {
      if (channel.categoryId) {
        const list = byCategory[channel.categoryId]
        if (list) list.push(channel)
        else byCategory[channel.categoryId] = [channel]
      } else {
        uncategorized.push(channel)
      }
    }

    return { categories: guildCategories, byCategory, uncategorized }
  }, [channels, categories, guildId])
}

export function useGuildMembers(guildId: string | null): GuildMember[] {
  const members = useAppStore((s) => s.members)

  return useMemo(() => {
    if (!guildId) return EMPTY_MEMBERS

    const list = Object.values(members)
      .filter((m) => m.guildId === guildId)
      .sort((a, b) => {
        const nameA = (a.nickname ?? a.user.displayName).toLowerCase()
        const nameB = (b.nickname ?? b.user.displayName).toLowerCase()
        return nameA.localeCompare(nameB, 'pt-BR')
      })

    return list.length > 0 ? list : EMPTY_MEMBERS
  }, [members, guildId])
}

/** Um membro específico. A entrada do dicionário já é referência estável. */
export function useMember(guildId: string | null, userId: string): GuildMember | undefined {
  return useAppStore((s) => (guildId ? s.members[`${guildId}:${userId}`] : undefined))
}

/**
 * Quantas mensagens chegaram depois da última leitura.
 *
 * Considera apenas o que está carregado em memória: um canal nunca aberto
 * nesta sessão devolve zero. Contar de verdade exigiria o servidor enviar o
 * número no bootstrap.
 */
export function useUnreadCount(channelId: string): number {
  const messages = useAppStore((s) => s.messages[channelId])
  const readState = useAppStore((s) => s.readStates[channelId])

  return useMemo(() => {
    if (!messages || messages.length === 0) return 0
    if (!readState?.lastReadMessageId) return messages.length

    const lastRead = BigInt(readState.lastReadMessageId)
    let count = 0
    // Percorre de trás para frente e para no primeiro já lido: numa lista de
    // centenas de mensagens, quase sempre são poucas iterações.
    for (let i = messages.length - 1; i >= 0; i--) {
      if (BigInt(messages[i]!.id) <= lastRead) break
      count++
    }
    return count
  }, [messages, readState])
}

/** Total de não lidas no aplicativo, para o contador no ícone. */
export function useTotalUnread(): number {
  const channels = useAppStore((s) => s.channels)
  const messages = useAppStore((s) => s.messages)
  const readStates = useAppStore((s) => s.readStates)

  return useMemo(() => {
    let total = 0
    for (const channel of Object.values(channels)) {
      if (channel.type !== 'text') continue

      const list = messages[channel.id]
      if (!list || list.length === 0) continue

      const read = readStates[channel.id]
      if (!read?.lastReadMessageId) {
        total += list.length
        continue
      }

      const lastRead = BigInt(read.lastReadMessageId)
      for (let i = list.length - 1; i >= 0; i--) {
        if (BigInt(list[i]!.id) <= lastRead) break
        total++
      }
    }
    return total
  }, [channels, messages, readStates])
}

/** Servidores com mensagem não lida, para o indicador da barra lateral. */
export function useUnreadGuildIds(): Set<string> {
  const channels = useAppStore((s) => s.channels)
  const messages = useAppStore((s) => s.messages)
  const readStates = useAppStore((s) => s.readStates)

  return useMemo(() => {
    const result = new Set<string>()
    for (const channel of Object.values(channels)) {
      if (channel.type !== 'text') continue

      const list = messages[channel.id]
      if (!list || list.length === 0) continue

      const read = readStates[channel.id]
      const last = list[list.length - 1]!
      if (!read?.lastReadMessageId || BigInt(last.id) > BigInt(read.lastReadMessageId)) {
        result.add(channel.guildId)
      }
    }
    return result
  }, [channels, messages, readStates])
}

/**
 * Quem está digitando no canal.
 *
 * As entradas expiram por tempo, então quem consome precisa reavaliar
 * periodicamente; `tick` existe para forçar isso de fora.
 */
export function useTypingUsers(channelId: string, tick = 0): string[] {
  const typing = useAppStore((s) => s.typing[channelId])

  return useMemo(() => {
    if (!typing || typing.length === 0) return EMPTY_IDS
    const now = Date.now()
    const active = typing.filter((t) => t.expiresAt > now).map((t) => t.userId)
    return active.length > 0 ? active : EMPTY_IDS
    // `tick` entra nas dependências justamente para reavaliar a expiração.
  }, [typing, tick])
}

/** Cargos do servidor, ordenados do mais alto para o mais baixo. */
export function useGuildRoles(guildId: string | null) {
  const roles = useAppStore((s) => s.roles)

  return useMemo(() => {
    if (!guildId) return []
    return Object.values(roles)
      .filter((r) => r.guildId === guildId)
      .sort((a, b) => b.position - a.position)
  }, [roles, guildId])
}

/**
 * Permissões efetivas de quem está usando, dentro de um servidor.
 *
 * O dono tem tudo por definição, sem depender de cargo. Para os demais, vale a
 * soma do @everyone com os cargos atribuídos, que é o mesmo cálculo que o
 * servidor faz antes de aceitar a ação. A conta vivia copiada dentro de um menu;
 * aqui ela fica num lugar só, e quem precisar da permissão não precisa repetir.
 */
export function useGuildPermissions(guildId: string | null): bigint {
  const myId = useAuthStore((s) => s.user?.id)
  const guild = useAppStore((s) => (guildId ? s.guilds[guildId] : null))
  const roles = useAppStore((s) => s.roles)
  const member = useMember(guildId ?? '', myId ?? '')

  return useMemo(() => {
    if (!guildId || !myId) return 0n
    if (guild?.ownerId === myId) return ~0n

    let bits = 0n
    const everyone = Object.values(roles).find((r) => r.guildId === guildId && r.isEveryone)
    if (everyone) bits |= BigInt(everyone.permissions)
    for (const roleId of member?.roleIds ?? []) {
      const role = roles[roleId]
      if (role) bits |= BigInt(role.permissions)
    }
    return bits
  }, [guildId, myId, guild?.ownerId, roles, member?.roleIds])
}
